import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import {
  api,
  type EngagementWorkspace, type WorkspaceTime, type WorklogState, type WorkspaceTimeRow,
  type WorkspaceMoney, type WorkspaceDeliverable,
  type WorkspaceProfitability, type ProfitabilityBucket, type WbsDeliverable,
} from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { fmtCurrency } from '../lib/fmt';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  ActionButton, Spinner, EmptyState, MetricCard,
} from '../components/ui';
import { EngagementContractPanel } from '../components/EngagementContractPanel';
import { BudgetTimelineChart } from '../components/BudgetTimelineChart';
import { WeeklyTimeChart } from '../components/WeeklyTimeChart';
import { GanttTimeline } from '../components/GanttTimeline';
import { useTableSort } from '../hooks/useTableSort';

// ─── Engagement workspace ───────────────────────────────────────────────────
//
// One screen for what an engagement is and how it is performing, instead of
// five reports and the joins held in someone's head.
//
// Phase 1: the shell, Overview, and Time. Money / Commercials / Delivery
// sections follow — the section router below is what they slot into.
//
// The page never decides who may see what: the server omits sections the
// caller isn't entitled to and says so in `visibility`, so this renders from
// the payload rather than re-implementing the permission model.

type Section = 'overview' | 'time' | 'money' | 'profitability' | 'delivery' | 'plan' | 'commercials';

const SECTIONS: Array<{ id: Section; label: string }> = [
  { id: 'overview',      label: 'Overview' },
  { id: 'time',          label: 'Time' },
  { id: 'money',         label: 'Money' },
  { id: 'profitability', label: 'Profitability' },
  { id: 'delivery',      label: 'Delivery' },
  { id: 'plan',          label: 'Plan' },
  { id: 'commercials',   label: 'Commercials' },
];

// ─── Worklog state presentation ─────────────────────────────────────────────
// `unbilled` is the one people come here for, so it gets the emphatic colour.
// `needs_review` is amber because it's a blocker, not a loss. `billed` is
// deliberately recessive: it's the resolved case and shouldn't compete.
const STATE: Record<WorklogState, { label: string; variant: 'green' | 'amber' | 'red' | 'gray' | 'blue'; help: string }> = {
  billed:       { label: 'Billed',       variant: 'green', help: 'On an issued invoice — matched through the worklog list captured when the invoice was written, not inferred from dates.' },
  unbilled:     { label: 'Unbilled',     variant: 'red',   help: 'Billable, reviewed, delivered — and on no invoice. This is revenue not yet charged for.' },
  needs_review: { label: 'Needs review', variant: 'amber', help: 'Flagged for delivery-manager review in Tempo. Cannot be billed until someone clears it.' },
  non_billable: { label: 'Non-billable', variant: 'gray',  help: 'Logged with no billable hours. Real work, deliberately not chargeable.' },
};

const STATE_ORDER: WorklogState[] = ['unbilled', 'needs_review', 'billed', 'non_billable'];

const fmtHours = (n: number): string => `${n.toFixed(2)}h`;

export default function AdminEngagementWorkspacePage() {
  const { id, section } = useParams<{ id: string; section?: string }>();
  const navigate = useNavigate();
  const { config } = useConfig();
  const active: Section = SECTIONS.some((s) => s.id === section) ? (section as Section) : 'overview';

  const wsQ = useQuery(() => api.engagementWorkspace.get(id!), [id]);

  if (wsQ.isLoading) return <Spinner />;
  if (wsQ.error) return <EmptyState message={wsQ.error} />;
  if (!wsQ.data) return null;
  const ws = wsQ.data;
  const e = ws.engagement;
  const jiraUrl = jiraIssueUrl(config, e.jira_issue_key);

  return (
    <>
      {/* ── Header: the five facts you need before reading anything else ── */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <SectionLabel>Engagement workspace</SectionLabel>
        <span style={{ fontSize: 14, fontWeight: 500 }}>{e.jira_issue_key}</span>
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{e.summary}</span>
        <Badge variant={e.status === 'Completed' ? 'green' : 'gray'}>{e.status ?? '—'}</Badge>
        {e.engagement_type && <Badge variant="blue">{e.engagement_type}</Badge>}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          {jiraUrl && (
            <a href={jiraUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', fontSize: 11 }}>
              Open in Jira ↗
            </a>
          )}
          <ExportAllButton ws={ws} />
          <ActionButton onClick={() => navigate('/admin/engagement-budget')} variant="default">
            Back to budget report
          </ActionButton>
        </span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {e.tempo_account_name ?? '—'}
        {e.tempo_account_id && e.tempo_account_id !== e.tempo_account_name && (
          <span title="Tempo account key — not a Jira work item key"> · {e.tempo_account_id}</span>
        )}
        {e.delivery_manager_name && <> · DM {e.delivery_manager_name}</>}
        {e.solution_architect_name && <> · SA {e.solution_architect_name}</>}
      </div>

      <HeaderTiles ws={ws} />

      <Card>
        <CardHeader>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              onClick={() => navigate(`/admin/engagements/${encodeURIComponent(id!)}/w/${s.id}`)}
              style={{
                background: 'none', border: 'none',
                borderBottom: active === s.id ? '2px solid var(--color-blue)' : '2px solid transparent',
                padding: '8px 16px', fontSize: 12, fontWeight: 500,
                color: active === s.id ? 'var(--color-text)' : 'var(--color-text-muted)',
                cursor: 'pointer',
              }}
            >
              {s.label}
            </button>
          ))}
        </CardHeader>

        {active === 'overview' && <OverviewSection ws={ws} />}
        {active === 'time' && <TimeSection engagementId={id!} engagementKey={e.jira_issue_key} />}
        {active === 'money' && (
          ws.visibility.money
            ? <MoneySection engagementId={id!} engagementKey={e.jira_issue_key} />
            : <EmptyState message="The money view needs the invoicing or receivables permission." />
        )}
        {active === 'profitability' && (
          ws.visibility.profitability
            ? <ProfitabilitySection engagementId={id!} engagementKey={e.jira_issue_key} />
            : <EmptyState message="Margin needs the profitability permission — cost data is granted deliberately." />
        )}
        {active === 'delivery' && <DeliverySection engagementId={id!} engagementKey={e.jira_issue_key} />}
        {active === 'plan' && <PlanSection engagement={e} />}
        {active === 'commercials' && <CommercialsSection ws={ws} />}
      </Card>
    </>
  );
}

/**
 * Whole-workspace export. Fetches every section the caller may see and emits
 * ONE file with labelled blocks, rather than making someone visit four tabs and
 * stitch four downloads together.
 *
 * Sections the caller can't see are simply absent — the export can't become a
 * side door around the permission gates, so a money-less viewer gets a file
 * without money in it rather than a 403 that loses them the rest.
 */
function ExportAllButton({ ws }: { ws: EngagementWorkspace }) {
  const [busy, setBusy] = useState(false);
  const e = ws.engagement;

  async function run() {
    setBusy(true);
    try {
      const [time, delivery, money] = await Promise.all([
        api.engagementWorkspace.time(e.jira_issue_id).catch(() => null),
        api.engagementWorkspace.delivery(e.jira_issue_id).catch(() => null),
        ws.visibility.money
          ? api.engagementWorkspace.money(e.jira_issue_id).catch(() => null)
          : Promise.resolve(null),
      ]);

      const out: (string | number)[][] = [];
      const block = (title: string, header: string[], body: (string | number)[][]) => {
        if (out.length) out.push([]);
        out.push([`## ${title}`]);
        out.push(header);
        out.push(...body);
      };

      block('Engagement', ['Field', 'Value'], [
        ['Key', e.jira_issue_key], ['Summary', e.summary ?? ''],
        ['Client', e.tempo_account_name ?? ''], ['Account key', e.tempo_account_id ?? ''],
        ['Status', e.status ?? ''], ['Type', e.engagement_type ?? ''],
        ['Delivery manager', e.delivery_manager_name ?? ''],
        ['Solution architect', e.solution_architect_name ?? ''],
        ['Ceiling', ws.ceiling ?? ''], ['Ceiling source', ws.ceiling_source ?? ''],
        ['Burned', e.burned_value], ['Remaining', ws.remaining ?? ''],
        ['% consumed', ws.pct_consumed ?? ''],
        ['Threshold passed', ws.thresholds?.worst ?? ''],
        ['Deliverables', `${e.completed_count} / ${e.deliverable_count}`],
        ['Created', e.created_date ?? ''], ['Kickoff', e.kickoff_date ?? ''],
        ['First work', e.first_work_date ?? ''], ['Last work', e.latest_work_date ?? ''],
      ]);

      if (ws.deal) {
        const d = ws.deal;
        block('Deal', ['Field', 'Value'], [
          ['Deal', d.deal_name ?? d.hubspot_deal_id], ['Value', d.amount ?? ''],
          ['Closed', d.close_date ?? ''], ['Type', d.deal_type ?? ''],
          ['Source', d.deal_source ?? ''], ['Owner', d.owner_name ?? ''],
          ['Onboarding', d.onboarding_status ?? ''],
        ]);
      }

      if (time) {
        block('Time by state', ['State', 'Hours', 'Value', 'Worklogs'],
          STATE_ORDER.map((s) => [STATE[s].label, time.by_state[s].hours, time.by_state[s].value, time.by_state[s].worklog_count]));
        block('Time by role', ['Role', 'Logged', 'Billed', 'Unbilled', 'Needs review', 'Non-billable', 'Value'],
          time.by_role.map((r) => [r.role, r.hours, r.billed_hours, r.unbilled_hours, r.needs_review_hours, r.non_billable_hours, r.value]));
        block('Time by person', ['Person', 'Logged', 'Billed', 'Unbilled', 'Needs review', 'Non-billable', 'Value'],
          time.by_person.map((p) => [p.name ?? '(unknown)', p.hours, p.billed_hours, p.unbilled_hours, p.needs_review_hours, p.non_billable_hours, p.value]));
        block('Time by week', ['Week starting', 'Logged', 'Billed', 'Unbilled', 'Needs review', 'Non-billable', 'Value'],
          time.by_week.map((w) => [w.week_start, w.hours, w.billed_hours, w.unbilled_hours, w.needs_review_hours, w.non_billable_hours, w.value]));
        block('Worklogs', ['Worklog ID', 'Date', 'Person', 'Deliverable', 'Logged', 'Billable', 'Non-billable', 'Value', 'State', 'Invoice', 'Description'],
          time.rows.map((r) => [r.tempo_worklog_id, r.work_date, r.author_name ?? '', r.deliverable_key ?? '',
            r.logged_hours, r.billable_hours, r.non_billable_hours, r.value, STATE[r.state].label,
            r.invoice_number ?? r.chargeover_invoice_id ?? '', r.description ?? '']));
      }

      if (delivery) {
        block('Deliverables', ['Key', 'Summary', 'Status', 'Due', 'Shipped', 'DM', 'SA', 'Logged', 'Billable', 'Rate', 'Value', 'Children', 'Last worked'],
          delivery.deliverables.map((d) => [d.jira_issue_key, d.summary ?? '', d.status ?? '', d.due_date ?? '',
            d.attribution_month ?? '', d.delivery_manager_name ?? '', d.solution_architect_name ?? '',
            d.logged_hours, d.billable_hours, d.billable_rate ?? '', d.value, d.child_issue_count, d.last_work_date ?? '']));
      }

      if (money) {
        const t = money.totals;
        block('Money totals', ['Field', 'Value'], [
          ['Account', money.tempo_account_name ?? ''],
          ['Covers other engagements', money.account_has_other_engagements ? 'yes' : 'no'],
          ['Billed', t.billed], ['Paid', t.paid], ['Credit applied', t.credit],
          ['Deposit used', t.deposit_applied], ['Written off', t.writeoff],
          ['Processing fees (not ours)', t.fees],
          ['Billed not yet split from fees', t.unsplit_billed],
          ['Settled off-invoice', t.settled_off_invoice],
          ['Outstanding', t.outstanding], ['Overdue', t.overdue], ['Draft', t.draft],
          ['Deposit remaining', t.deposit_remaining], ['Credit remaining', t.credit_remaining],
        ]);
        block('Invoices', ['Invoice', 'Status', 'Issued', 'Due', 'Days past due', 'Total', 'Billed net', 'Paid', 'Credit', 'Write-off', 'Deposit applied', 'Balance collectable', 'Balance gross'],
          money.invoices.map((i) => [i.invoice_number ?? i.chargeover_invoice_id, i.raw_status ?? '',
            i.date_issued ?? i.invoice_date ?? '', i.due_date ?? '', i.days_past_due ?? '',
            i.total_amount, i.billed_net, i.paid_amount, i.credit_amount, i.writeoff_amount,
            i.deposit_applied, i.balance_net, i.balance_amount]));
        block('Transactions', ['Date', 'Type', 'Deposit', 'Status', 'Amount', 'Applied', 'Remaining'],
          money.transactions.map((x) => [x.transaction_date ?? '', x.transaction_type ?? '',
            x.is_deposit ? 'yes' : 'no', x.raw_status ?? '', x.amount, x.applied_amount, x.remaining_amount]));
      }

      downloadCsv(`${e.jira_issue_key}-workspace.csv`, out);
    } finally { setBusy(false); }
  }

  return (
    <ActionButton onClick={run} disabled={busy} variant="default">
      {busy ? 'Exporting…' : 'Export everything'}
    </ActionButton>
  );
}

function HeaderTiles({ ws }: { ws: EngagementWorkspace }) {
  const e = ws.engagement;
  const worst = ws.thresholds?.worst;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10, marginBottom: 10 }}>
      <MetricCard
        label="Ceiling"
        value={ws.ceiling != null ? fmtCurrency(ws.ceiling) : '—'}
        sub={
          ws.ceiling_source === 'contract' ? 'From captured contract terms'
            : ws.ceiling_source === 'jira' ? 'Jira budget field — no contract captured'
            : ws.visibility.contract ? 'No budget or contract set' : 'Not visible to you'
        }
        accent={ws.ceiling_source === 'jira' ? 'amber' : undefined}
      />
      <MetricCard label="Burned" value={fmtCurrency(e.burned_value)} sub="Billable hours × deliverable rate" />
      <MetricCard
        label="Remaining"
        value={ws.remaining != null ? fmtCurrency(ws.remaining) : '—'}
        sub={ws.pct_consumed != null ? `${ws.pct_consumed}% consumed` : 'No ceiling to measure against'}
        accent={ws.remaining != null && ws.remaining < 0 ? 'amber' : undefined}
      />
      <MetricCard
        label="Contract status"
        value={
          worst === 'not_to_exceed' ? 'Past SNE'
            : worst === 'fixed_fee' ? 'Over fee'
            : worst === 'high' ? 'Past high'
            : worst === 'median' ? 'Past median'
            : worst === 'low' ? 'Past low'
            : ws.thresholds ? 'Within band'
            : '—'
        }
        accent={worst === 'not_to_exceed' || worst === 'fixed_fee' ? 'amber' : ws.thresholds ? 'green' : undefined}
        sub={ws.thresholds ? 'Against terms in force today' : ws.visibility.contract ? 'No contract captured' : 'Not visible to you'}
      />
    </div>
  );
}

// ─── Overview ───────────────────────────────────────────────────────────────

function OverviewSection({ ws }: { ws: EngagementWorkspace }) {
  const e = ws.engagement;
  const tlQ = useQuery(() => api.admin.engagementBudgetTimeline(e.jira_issue_id), [e.jira_issue_id]);

  const facts: Array<[string, string]> = [
    ['Deliverables', `${e.completed_count} / ${e.deliverable_count} complete`],
    ['Engagement created', e.created_date ?? '—'],
    ['Welcome email', e.welcome_email_sent ?? '—'],
    ['Pre-kickoff', e.pre_kickoff_date ?? '—'],
    ['Kickoff', e.kickoff_date ?? '—'],
    ['First work logged', e.first_work_date ?? '—'],
    ['Last work logged', e.latest_work_date ?? '—'],
  ];

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        {facts.map(([k, v]) => (
          <div key={k}>
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{k}</div>
            <div style={{ fontSize: 12 }}>{v}</div>
          </div>
        ))}
      </div>

      {ws.visibility.contract ? (
        tlQ.isLoading ? <Spinner />
          : tlQ.error ? <EmptyState message={tlQ.error} />
          : tlQ.data ? <BudgetTimelineChart timeline={tlQ.data} />
          : null
      ) : (
        <EmptyState message="Budget over time needs the engagement budget permission." />
      )}
    </div>
  );
}

// ─── Commercials ────────────────────────────────────────────────────────────

function CommercialsSection({ ws }: { ws: EngagementWorkspace }) {
  const d = ws.deal;
  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Originating deal</div>
        {!d ? (
          <EmptyState message="No deal tied to this engagement. Work that never came through a tracked deal, or an onboarding record that hasn't been associated yet." />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            {([
              ['Deal', d.deal_name ?? d.hubspot_deal_id],
              ['Value', d.amount != null ? fmtCurrency(d.amount) : '—'],
              ['Closed', d.close_date ?? '—'],
              ['Type', d.deal_type ?? '—'],
              ['Source', d.deal_source ?? '—'],
              ['Owner', d.owner_name ?? '—'],
              ['Onboarding', d.onboarding_status ?? '—'],
            ] as Array<[string, string]>).map(([k, v]) => (
              <div key={k}>
                <div style={{ fontSize: 10, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{k}</div>
                <div style={{ fontSize: 12 }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {ws.visibility.contract
        ? <EngagementContractPanel engagementId={ws.engagement.jira_issue_id} showTimeline={false} />
        : <EmptyState message="Contract terms need the engagement budget permission." />}
    </div>
  );
}

// ─── Money ──────────────────────────────────────────────────────────────────
// Figures come from buildInvoiceReport scoped to the account, so this screen
// and /admin/invoicing run the same netting and cannot disagree.

function MoneySection({ engagementId, engagementKey }: { engagementId: string; engagementKey: string }) {
  const { data, isLoading, error } = useQuery(() => api.engagementWorkspace.money(engagementId), [engagementId]);
  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return <EmptyState message="No Tempo account on this engagement, so nothing in ChargeOver can be tied to it." />;

  const t = data.totals;
  const a = data.ageing;
  const ageBands: Array<[string, number]> = [
    ['1–30d', a.d1_30], ['31–60d', a.d31_60], ['61–90d', a.d61_90], ['91+d', a.d91_plus],
  ];

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Invoices belong to a ChargeOver customer, not an engagement. Saying so
          is the difference between a number someone can trust and one they
          quietly misread as this engagement's alone. */}
      {data.account_has_other_engagements && (
        <div style={{ fontSize: 11, color: 'var(--color-amber-text)' }}>
          This account carries invoices for more than one engagement — the figures below are
          for the whole account ({data.tempo_account_name}), not this engagement alone.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <MetricCard
          label="Billed (services)"
          value={fmtCurrency(t.billed)}
          sub={t.unsplit_billed > 0
            ? `${fmtCurrency(t.unsplit_billed)} not yet split from fees`
            : 'Deposits and fees excluded'}
          accent={t.unsplit_billed > 0 ? 'amber' : undefined}
        />
        <MetricCard label="Paid" value={fmtCurrency(t.paid)} sub="Cash received" />
        <MetricCard
          label="Outstanding"
          value={fmtCurrency(t.outstanding)}
          accent={t.outstanding > 0 ? 'amber' : 'green'}
          sub={t.overdue > 0 ? `${fmtCurrency(t.overdue)} overdue` : 'Nothing overdue'}
        />
        <MetricCard
          label="Unapplied credit"
          value={fmtCurrency(t.deposit_remaining + t.credit_remaining)}
          sub={`${fmtCurrency(t.deposit_remaining)} deposit · ${fmtCurrency(t.credit_remaining)} credit`}
        />
      </div>

      {/* Fees are called out on their own line, not tucked into the muted row
          below with write-offs and credits. They are the only figure here that
          is not ours: it goes to the payment processor and cannot be spent on
          delivery, which is exactly why billed must never include it. */}
      {(t.fees > 0 || t.unsplit_billed > 0) && (
        <div style={{
          fontSize: 11, padding: '8px 10px', borderRadius: 'var(--radius-sm)',
          background: 'var(--color-bg)', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline',
        }}>
          <span>
            <b>{fmtCurrency(t.fees)}</b> in processing fees billed on top of services
          </span>
          <span style={{ color: 'var(--color-text-muted)' }}>
            Passes through to the payment processor — not available to deliver with, and excluded
            from every figure above.
          </span>
          {t.unsplit_billed > 0 && (
            <span style={{ color: 'var(--color-amber-text)' }}>
              {fmtCurrency(t.unsplit_billed)} of billed is on invoices with no line-item split, so
              any fee on those is still counted inside it.
            </span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: 'var(--color-text-muted)' }}>
        <span>Credit applied {fmtCurrency(t.credit)}</span>
        <span>Deposit used {fmtCurrency(t.deposit_applied)}</span>
        <span>Written off {fmtCurrency(t.writeoff)}</span>
        <span>Draft {fmtCurrency(t.draft)}</span>
        {/* Surfaced, not hidden: a non-zero figure here means ChargeOver
            settled something in a way ORP cannot see. */}
        {t.settled_off_invoice > 0 && (
          <span title="ChargeOver marks these invoices paid but its amount columns don't account for it. Treated as settled.">
            Settled off-invoice {fmtCurrency(t.settled_off_invoice)}
          </span>
        )}
      </div>

      {t.overdue > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 4 }}>Overdue by age</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {ageBands.map(([label, v]) => (
              <div key={label} style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 4,
                border: '0.5px solid var(--color-border-strong)',
                color: v > 0 ? 'var(--color-red-text)' : 'var(--color-text-hint)',
              }}>
                {label} {v > 0 ? fmtCurrency(v) : '—'}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Invoices</span>
        <Badge variant="gray">{data.invoices.length}</Badge>
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={() => exportMoney(engagementKey, data)} variant="default">Export CSV</ActionButton>
        </span>
      </div>
      {data.invoices.length === 0 ? <EmptyState message="No invoices on this account." /> : (
        <div style={{ maxHeight: 340, overflow: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <Th>Invoice</Th><Th>Status</Th><Th>Issued</Th><Th>Due</Th>
                <Th right>Total</Th><Th right>Services</Th><Th right>Fees</Th>
                <Th right>Paid</Th><Th right>Credit</Th><Th right>Balance</Th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((i) => (
                <Tr key={i.chargeover_invoice_id}>
                  <Td bold>{i.invoice_number ?? i.chargeover_invoice_id}</Td>
                  <Td><Badge variant={i.lifecycle === 'paid' ? 'green' : i.lifecycle === 'overdue' ? 'red' : i.lifecycle === 'draft' || i.lifecycle === 'void' ? 'gray' : 'blue'}>{i.raw_status ?? i.lifecycle}</Badge></Td>
                  <Td muted style={{ fontSize: 11 }}>{i.date_issued ?? i.invoice_date ?? '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>
                    {i.due_date ?? '—'}
                    {i.days_past_due != null && i.days_past_due > 0 && (
                      <span style={{ marginLeft: 5, color: 'var(--color-red-text)', fontSize: 10 }}>+{i.days_past_due}d</span>
                    )}
                  </Td>
                  <Td right>{fmtCurrency(i.total_amount)}</Td>
                  {/* Null is "ChargeOver hasn't given us line items", not zero.
                      A dash keeps that distinct from a genuine no-fee invoice. */}
                  <Td right>
                    {i.services_amount != null
                      ? fmtCurrency(i.services_amount)
                      : <span style={{ color: 'var(--color-amber-text)' }} title="No line items from ChargeOver yet — run the invoice line-item backfill on Admin → Data sync">—</span>}
                  </Td>
                  <Td right muted>
                    {i.services_amount == null ? '—'
                      : i.fees_amount && i.fees_amount > 0 ? fmtCurrency(i.fees_amount)
                      : '—'}
                  </Td>
                  <Td right muted>{fmtCurrency(i.paid_amount)}</Td>
                  <Td right muted>{i.credit_amount > 0 ? fmtCurrency(i.credit_amount) : '—'}</Td>
                  <Td right bold style={{ color: i.balance_net > 0 ? 'var(--color-red-text)' : 'var(--color-text-muted)' }}>
                    {fmtCurrency(i.balance_net)}
                    {i.deposit_applied > 0 && (
                      <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: 'var(--color-text-muted)' }}>
                        {fmtCurrency(i.deposit_applied)} from deposit
                      </span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 500 }}>Payments, credits and deposits</div>
      {data.transactions.length === 0 ? <EmptyState message="No transactions on this account." /> : (
        <div style={{ maxHeight: 280, overflow: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <Th>Date</Th><Th>Type</Th><Th>Status</Th>
                <Th right>Amount</Th><Th right>Applied</Th><Th right>Remaining</Th>
              </tr>
            </thead>
            <tbody>
              {data.transactions.map((x) => (
                <Tr key={x.chargeover_transaction_id}>
                  <Td muted style={{ fontSize: 11 }}>{x.transaction_date ?? '—'}</Td>
                  <Td>
                    {x.is_deposit
                      ? <Badge variant="blue">Deposit</Badge>
                      : <Badge variant="gray">{x.transaction_type ?? '—'}</Badge>}
                  </Td>
                  <Td muted style={{ fontSize: 11 }}>{x.raw_status ?? '—'}</Td>
                  <Td right>{fmtCurrency(x.amount)}</Td>
                  <Td right muted>{fmtCurrency(x.applied_amount)}</Td>
                  <Td right style={{ color: x.remaining_amount > 0 ? 'var(--color-green-text)' : undefined }}>
                    {x.remaining_amount > 0 ? fmtCurrency(x.remaining_amount) : '—'}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

function exportMoney(engagementKey: string, m: WorkspaceMoney): void {
  const rows: (string | number)[][] = [[
    'Invoice', 'Status', 'Lifecycle', 'Issued', 'Due', 'Days past due',
    'Total', 'Billed (net of deposit)', 'Paid', 'Credit', 'Write-off',
    'Deposit applied', 'Settled off-invoice', 'Balance (collectable)', 'Balance (ChargeOver gross)',
  ]];
  for (const i of m.invoices) {
    rows.push([
      i.invoice_number ?? i.chargeover_invoice_id, i.raw_status ?? '', i.lifecycle,
      i.date_issued ?? i.invoice_date ?? '', i.due_date ?? '', i.days_past_due ?? '',
      i.total_amount.toFixed(2), i.billed_net.toFixed(2), i.paid_amount.toFixed(2),
      i.credit_amount.toFixed(2), i.writeoff_amount.toFixed(2),
      i.deposit_applied.toFixed(2), i.settled_off_invoice.toFixed(2),
      i.balance_net.toFixed(2), i.balance_amount.toFixed(2),
    ]);
  }
  downloadCsv(`${engagementKey}-money.csv`, rows);
}

// ─── Plan ───────────────────────────────────────────────────────────────────
// The Gantt, scoped to this engagement. Reuses the timeline component and the
// gantt endpoint's existing `engagement` mode wholesale rather than growing a
// second timeline implementation that would drift from the first.

/** Frame the chart on the WORK, not on a fixed trailing window: an engagement
 *  that ran last year should open showing itself, not an empty six months. */
function planWindow(e: EngagementWorkspace['engagement']): { from: string; to: string } {
  const today = new Date();
  const pad = (d: Date, months: number) => {
    const c = new Date(d);
    c.setUTCMonth(c.getUTCMonth() + months);
    return c.toISOString().slice(0, 10);
  };
  const firstWork = e.first_work_date ? new Date(`${e.first_work_date}T00:00:00Z`) : null;
  const lastWork = e.latest_work_date ? new Date(`${e.latest_work_date}T00:00:00Z`) : null;
  const start = firstWork ?? new Date(today.getTime() - 180 * 86_400_000);
  // Run to whichever is later — the last worklog or today — so an active
  // engagement always shows the present, and a finished one doesn't trail off
  // into months of blank future.
  const end = lastWork && lastWork > today ? lastWork : today;
  return { from: pad(start, -1), to: pad(end, 1) };
}

function PlanSection({ engagement }: { engagement: EngagementWorkspace['engagement'] }) {
  const { from, to } = useMemo(() => planWindow(engagement), [engagement]);
  const { data, isLoading, error } = useQuery(
    () => api.gantt.get({ mode: 'engagement', id: engagement.jira_issue_id, from, to }),
    [engagement.jira_issue_id, from, to],
  );

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
        {from} → {to}. Planned bars come from the Jira start-target and planned-ship dates;
        the heat shows where hours actually landed.
      </div>
      <GanttTimeline
        mode="engagement"
        from={from}
        to={to}
        worklogs={data.worklogs}
        deliverables={data.deliverables}
        childIssues={data.children}
        engagements={data.engagements}
      />
    </div>
  );
}

// ─── Profitability ──────────────────────────────────────────────────────────
// Revenue is billable hours × the deliverable's rate — the same basis as burn,
// so this can't disagree with the budget report. Cost is LOGGED hours × the
// blended rate in force when the work happened: non-billable time was paid for
// too, and costing only billable hours would report a margin never earned.

type ProfitView = 'role' | 'person' | 'deliverable' | 'month';

function ProfitabilitySection({ engagementId, engagementKey }: { engagementId: string; engagementKey: string }) {
  const { data, isLoading, error } = useQuery(() => api.engagementWorkspace.profitability(engagementId), [engagementId]);
  const [view, setView] = useState<ProfitView>('deliverable');

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;
  if (data.logged_hours === 0) return <EmptyState message="No time logged against this engagement." />;

  const rows = view === 'role'
    ? data.by_role.map((r) => ({ key: r.role, label: r.role, ...r }))
    : view === 'person'
      ? data.by_person.map((p) => ({ key: p.jira_user_id ?? p.name ?? '?', label: p.name ?? '(unknown)', ...p }))
      : view === 'deliverable'
        ? data.by_deliverable.map((d) => ({ key: d.jira_issue_id, label: `${d.jira_issue_key ?? d.jira_issue_id} ${d.summary ?? ''}`, ...d }))
        : data.by_month.map((m) => ({ key: m.month, label: m.month, ...m }));

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Loud, because every figure below is wrong-low on cost until it's
          resolved. A quiet footnote would let someone quote the margin. */}
      {data.uncosted_hours > 0 && (
        <div style={{ fontSize: 11, color: 'var(--color-amber-text)' }}>
          {data.uncosted_hours.toFixed(2)}h of logged time falls before the earliest blended cost
          rate, so no cost is attributed to it. Margin below is overstated until a rate covers
          that period — set one on Admin → Blended cost rate.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <MetricCard label="Revenue" value={fmtCurrency(data.revenue)} sub="Billable hours × rate" />
        <MetricCard
          label="Cost"
          value={fmtCurrency(data.cost)}
          sub={data.effective_cost_rate != null
            ? `${data.logged_hours.toFixed(0)}h logged @ ${fmtCurrency(data.effective_cost_rate)}/h`
            : 'No cost rate set'}
        />
        <MetricCard
          label="Margin"
          value={fmtCurrency(data.margin)}
          accent={data.margin < 0 ? 'amber' : 'green'}
          sub={data.margin < 0 ? 'Delivered at a loss' : 'Revenue less cost'}
        />
        <MetricCard
          label="Margin %"
          value={data.margin_pct != null ? `${data.margin_pct}%` : '—'}
          accent={data.margin_pct == null ? undefined : data.margin_pct < 0 ? 'amber' : 'green'}
          sub={data.margin_pct == null ? 'No revenue to measure against' : 'Of revenue'}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {([['role', 'By role'], ['deliverable', 'By deliverable'], ['person', 'By person'], ['month', 'By month']] as Array<[ProfitView, string]>).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              border: view === id ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
              background: 'var(--color-surface)',
              color: view === id ? 'var(--color-text)' : 'var(--color-text-muted)',
            }}
          >
            {label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={() => exportProfit(engagementKey, data)} variant="default">Export CSV</ActionButton>
        </span>
      </div>

      <div style={{ maxHeight: 'calc(100vh - 480px)', overflow: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <Th>{view === 'role' ? 'Role' : view === 'person' ? 'Person' : view === 'deliverable' ? 'Deliverable' : 'Month'}</Th>
              <Th right>Logged</Th><Th right>Billable</Th>
              <Th right>Revenue</Th><Th right>Cost</Th><Th right>Margin</Th><Th right>Margin %</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.key}>
                <Td bold>{r.label}</Td>
                <Td right muted>{r.logged_hours.toFixed(2)}</Td>
                <Td right muted>{r.billable_hours.toFixed(2)}</Td>
                <Td right>{fmtCurrency(r.revenue)}</Td>
                <Td right muted>{fmtCurrency(r.cost)}</Td>
                <Td right bold style={{ color: r.margin < 0 ? 'var(--color-red-text)' : undefined }}>
                  {fmtCurrency(r.margin)}
                </Td>
                <Td right style={{ color: r.margin_pct != null && r.margin_pct < 0 ? 'var(--color-red-text)' : undefined }}>
                  {r.margin_pct != null ? `${r.margin_pct}%` : '—'}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function exportProfit(engagementKey: string, p: WorkspaceProfitability): void {
  const out: (string | number)[][] = [['Grouping', 'Name', 'Logged hours', 'Billable hours', 'Revenue', 'Cost', 'Margin', 'Margin %']];
  const push = (g: string, name: string, b: ProfitabilityBucket) =>
    out.push([g, name, b.logged_hours, b.billable_hours, b.revenue, b.cost, b.margin, b.margin_pct ?? '']);
  push('Total', engagementKey, p);
  for (const r of p.by_role) push('Role', r.role, r);
  for (const d of p.by_deliverable) push('Deliverable', `${d.jira_issue_key ?? d.jira_issue_id} ${d.summary ?? ''}`, d);
  for (const x of p.by_person) push('Person', x.name ?? '(unknown)', x);
  for (const m of p.by_month) push('Month', m.month, m);
  downloadCsv(`${engagementKey}-profitability.csv`, out);
}

// ─── Delivery ───────────────────────────────────────────────────────────────

function DeliverySection({ engagementId, engagementKey }: { engagementId: string; engagementKey: string }) {
  const { config } = useConfig();
  const { data, isLoading, error } = useQuery(() => api.engagementWorkspace.delivery(engagementId), [engagementId]);
  const [showWbs, setShowWbs] = useState(false);
  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  const rows = data?.deliverables ?? [];
  if (rows.length === 0) return <EmptyState message="No deliverables under this engagement." />;

  const shipped = rows.filter((d) => d.attribution_month != null).length;
  const totalValue = rows.reduce((s, d) => s + d.value, 0);

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 11, color: 'var(--color-text-muted)' }}>
        <span><b>{shipped}</b> of <b>{rows.length}</b> shipped</span>
        <span>Total value {fmtCurrency(totalValue)}</span>
        <label style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
          <input type="checkbox" checked={showWbs} onChange={(ev) => setShowWbs(ev.target.checked)} />
          Break down by work item
        </label>
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={() => exportDelivery(engagementKey, rows)} variant="default">Export CSV</ActionButton>
        </span>
      </div>

      {showWbs && <WbsTree nodes={data?.wbs ?? []} engagementKey={engagementKey} />}
      <div style={{ maxHeight: 'calc(100vh - 420px)', overflow: 'auto' }}>
        <Table>
          <thead>
            <tr>
              <Th>Deliverable</Th><Th>Status</Th><Th>Due</Th><Th>Shipped</Th>
              <Th>DM</Th><Th>SA</Th>
              <Th right>Logged</Th><Th right>Billable</Th><Th right>Value</Th>
              <Th right>Children</Th><Th>Last worked</Th><Th>Jira</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const url = jiraIssueUrl(config, d.jira_issue_key);
              return (
                <Tr key={d.jira_issue_id}>
                  <Td bold>
                    <span style={{ marginRight: 6 }}>{d.jira_issue_key}</span>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{d.summary}</span>
                  </Td>
                  <Td><Badge variant={d.attribution_month ? 'green' : 'gray'}>{d.status ?? '—'}</Badge></Td>
                  <Td muted style={{ fontSize: 11 }}>{d.due_date ?? '—'}</Td>
                  {/* attribution_month IS what "shipped" means in ORP — the
                      month the deliverable's value was attributed to. */}
                  <Td muted style={{ fontSize: 11 }}>{d.attribution_month ?? '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>{d.delivery_manager_name ?? '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>{d.solution_architect_name ?? '—'}</Td>
                  <Td right muted style={{ fontSize: 11 }}>{d.logged_hours.toFixed(2)}</Td>
                  <Td right style={{ fontSize: 11 }}>{d.billable_hours.toFixed(2)}</Td>
                  <Td right>{fmtCurrency(d.value)}</Td>
                  <Td right muted style={{ fontSize: 11 }}>{d.child_issue_count || '—'}</Td>
                  <Td muted style={{ fontSize: 11 }}>{d.last_work_date ?? '—'}</Td>
                  <Td>{url ? <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', fontSize: 11 }}>Open ↗</a> : '—'}</Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

/**
 * Burn down the deliverable → child-issue tree. Each deliverable row is the
 * roll-up of its children plus any time logged directly on it, so a parent
 * always equals what sits beneath it.
 *
 * The workstream level above deliverables is absent on purpose: Jira has it and
 * ORP stores only its id, so a node would read as a bare number. Called out
 * here rather than left as a silent gap in the hierarchy.
 */
function WbsTree({ nodes, engagementKey }: { nodes: WbsDeliverable[]; engagementKey: string }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (nodes.length === 0) return <EmptyState message="No time logged to break down." />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Work breakdown</span>
        <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>
          Deliverable → work item. Workstreams aren’t shown — Jira has them, ORP stores only their id.
        </span>
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={() => exportWbs(engagementKey, nodes)} variant="default">Export CSV</ActionButton>
        </span>
      </div>
      <div style={{ maxHeight: 420, overflow: 'auto', border: '0.5px solid var(--color-border)' }}>
        <Table>
          <thead>
            <tr>
              <Th>Work item</Th><Th>Type</Th>
              <Th right>Estimate</Th><Th right>Logged</Th><Th right>Variance</Th>
              <Th right>Billable</Th><Th right>Value</Th><Th right>Worklogs</Th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((d) => {
              const isOpen = open[d.jira_issue_id] ?? false;
              const expandable = d.children.length > 0;
              return [
                <Tr
                  key={d.jira_issue_id}
                  onClick={expandable ? () => setOpen((o) => ({ ...o, [d.jira_issue_id]: !isOpen })) : undefined}
                >
                  <Td bold>
                    <span style={{ display: 'inline-block', width: 12, color: 'var(--color-text-muted)' }}>
                      {expandable ? (isOpen ? '▾' : '▸') : ''}
                    </span>
                    <span style={{ marginRight: 6 }}>{d.jira_issue_key ?? d.jira_issue_id}</span>
                    <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{d.summary}</span>
                  </Td>
                  <Td>
                    <Badge variant={d.attribution_month ? 'green' : 'gray'}>{d.status ?? '—'}</Badge>
                  </Td>
                  <Td right muted>
                    <EstimateCell own={d.estimate_hours} fromChildren={d.children_estimate_hours} />
                  </Td>
                  <Td right>{d.logged_hours.toFixed(2)}</Td>
                  <Td right><VarianceCell hours={d.variance_hours} /></Td>
                  <Td right muted>{d.billable_hours.toFixed(2)}</Td>
                  <Td right>{fmtCurrency(d.value)}</Td>
                  <Td right muted style={{ fontSize: 11 }}>{d.worklog_count}</Td>
                </Tr>,
                ...(isOpen ? d.children.map((c) => (
                  <Tr key={`${d.jira_issue_id}-${c.jira_issue_id}`}>
                    <Td style={{ paddingLeft: 40, fontSize: 11 }}>
                      <span style={{ marginRight: 6 }}>{c.jira_issue_key ?? c.jira_issue_id}</span>
                      <span style={{ color: 'var(--color-text-muted)' }}>{c.summary}</span>
                    </Td>
                    <Td muted style={{ fontSize: 11 }}>{c.issue_type ?? '—'}</Td>
                    <Td right muted style={{ fontSize: 11 }}>
                      <EstimateCell own={c.estimate_hours} fromChildren={null} />
                    </Td>
                    <Td right muted style={{ fontSize: 11 }}>{c.logged_hours.toFixed(2)}</Td>
                    <Td right style={{ fontSize: 11 }}><VarianceCell hours={c.variance_hours} /></Td>
                    <Td right muted style={{ fontSize: 11 }}>{c.billable_hours.toFixed(2)}</Td>
                    <Td right muted style={{ fontSize: 11 }}>{fmtCurrency(c.value)}</Td>
                    <Td right muted style={{ fontSize: 11 }}>{c.worklog_count}</Td>
                  </Tr>
                )) : []),
                // Time logged straight on the deliverable, shown as its own
                // line rather than folded into a fake child — every other row
                // in this tree is a real Jira issue.
                ...(isOpen && d.direct_hours > 0 ? [(
                  <Tr key={`${d.jira_issue_id}-direct`}>
                    <Td style={{ paddingLeft: 40, fontSize: 11, color: 'var(--color-text-muted)' }}>
                      Logged directly on the deliverable
                    </Td>
                    <Td muted style={{ fontSize: 11 }}>—</Td>
                    <Td muted>—</Td>
                    <Td right muted style={{ fontSize: 11 }}>{d.direct_hours.toFixed(2)}</Td>
                    <Td muted>—</Td><Td muted>—</Td><Td muted>—</Td><Td muted>—</Td>
                  </Tr>
                )] : []),
              ];
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

/** "Not estimated" is a different fact from "estimated at zero", so it renders
 *  as an explicit dash in hint ink rather than a number. */
function EstimateCell({ own, fromChildren }: { own: number | null; fromChildren: number | null }) {
  if (own != null) {
    return (
      <span>
        {own.toFixed(2)}
        {/* Both figures exist and disagree — the deliverable's own plan and
            what its parts add up to. That gap is worth seeing, not smoothing. */}
        {fromChildren != null && Math.abs(fromChildren - own) > 0.01 && (
          <span
            style={{ display: 'block', fontSize: 10, color: 'var(--color-amber-text)' }}
            title="The deliverable's own estimate and the sum of its work items disagree. Both are entered by hand in Jira."
          >
            parts {fromChildren.toFixed(2)}
          </span>
        )}
      </span>
    );
  }
  if (fromChildren != null) {
    return (
      <span title="Rolled up from the work items — the deliverable itself carries no estimate.">
        {fromChildren.toFixed(2)}
        <span style={{ display: 'block', fontSize: 10, color: 'var(--color-text-muted)' }}>from parts</span>
      </span>
    );
  }
  return <span style={{ color: 'var(--color-text-hint)' }} title="Not estimated in Jira">—</span>;
}

function VarianceCell({ hours }: { hours: number | null }) {
  if (hours == null) return <span style={{ color: 'var(--color-text-hint)' }}>—</span>;
  const over = hours > 0.01;
  return (
    <span style={{ color: over ? 'var(--color-red-text)' : hours < -0.01 ? 'var(--color-green-text)' : undefined }}>
      {over ? '+' : ''}{hours.toFixed(2)}
    </span>
  );
}

function exportWbs(engagementKey: string, nodes: WbsDeliverable[]): void {
  const out: (string | number)[][] = [[
    'Level', 'Deliverable', 'Work item', 'Type', 'Estimate hours', 'Estimate from parts',
    'Logged hours', 'Variance hours', 'Billable hours', 'Value', 'Worklogs',
  ]];
  // Blank, not 0, where there is no estimate — a spreadsheet reader must not
  // average unestimated rows in as zeroes.
  const n = (v: number | null) => (v == null ? '' : v);
  for (const d of nodes) {
    const dl = `${d.jira_issue_key ?? d.jira_issue_id} ${d.summary ?? ''}`;
    out.push(['Deliverable', dl, '', d.status ?? '', n(d.estimate_hours), n(d.children_estimate_hours),
      d.logged_hours, n(d.variance_hours), d.billable_hours, d.value, d.worklog_count]);
    for (const c of d.children) {
      out.push(['Work item', dl, `${c.jira_issue_key ?? c.jira_issue_id} ${c.summary ?? ''}`,
        c.issue_type ?? '', n(c.estimate_hours), '', c.logged_hours, n(c.variance_hours),
        c.billable_hours, c.value, c.worklog_count]);
    }
    if (d.direct_hours > 0) {
      out.push(['Direct', dl, '(logged on the deliverable)', '', '', '', d.direct_hours, '', '', '', '']);
    }
  }
  downloadCsv(`${engagementKey}-work-breakdown.csv`, out);
}

function exportDelivery(engagementKey: string, rows: WorkspaceDeliverable[]): void {
  const out: (string | number)[][] = [[
    'Deliverable', 'Summary', 'Status', 'Due', 'Shipped (attribution month)',
    'Delivery manager', 'Solution architect', 'Logged hours', 'Billable hours',
    'Rate', 'Value', 'Child issues', 'Last worked',
  ]];
  for (const d of rows) {
    out.push([
      d.jira_issue_key, d.summary ?? '', d.status ?? '', d.due_date ?? '',
      d.attribution_month ?? '', d.delivery_manager_name ?? '', d.solution_architect_name ?? '',
      d.logged_hours.toFixed(2), d.billable_hours.toFixed(2),
      d.billable_rate != null ? d.billable_rate.toFixed(2) : '',
      d.value.toFixed(2), d.child_issue_count, d.last_work_date ?? '',
    ]);
  }
  downloadCsv(`${engagementKey}-delivery.csv`, out);
}

// ─── Time ───────────────────────────────────────────────────────────────────

type TimeView = 'role' | 'person' | 'deliverable' | 'week' | 'worklogs';

function TimeSection({ engagementId, engagementKey }: { engagementId: string; engagementKey: string }) {
  const { data, isLoading, error } = useQuery(() => api.engagementWorkspace.time(engagementId), [engagementId]);
  const [view, setView] = useState<TimeView>('person');
  const [stateFilter, setStateFilter] = useState<WorklogState | 'all'>('all');

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;
  if (data.logged_hours === 0) return <EmptyState message="No time logged against this engagement." />;

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <StateTiles time={data} active={stateFilter} onPick={(s) => { setStateFilter(s); setView('worklogs'); }} />

      <WeeklyTimeChart time={data} />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {([
          ['role', 'By role'], ['person', 'By person'], ['deliverable', 'By deliverable'],
          ['week', 'By week'], ['worklogs', 'Worklogs'],
        ] as Array<[TimeView, string]>).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setView(id)}
            style={{
              fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
              border: view === id ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
              background: view === id ? 'var(--color-blue-bg, var(--color-surface))' : 'var(--color-surface)',
              color: view === id ? 'var(--color-text)' : 'var(--color-text-muted)',
            }}
          >
            {label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto' }}>
          <ActionButton onClick={() => exportTime(engagementKey, data)} variant="default">Export CSV</ActionButton>
        </span>
      </div>

      {view === 'worklogs'
        ? <WorklogTable rows={data.rows} filter={stateFilter} onFilter={setStateFilter} truncated={data.rows_truncated} />
        : <BucketTable time={data} view={view} />}
    </div>
  );
}

function StateTiles({ time, active, onPick }: {
  time: WorkspaceTime; active: WorklogState | 'all'; onPick: (s: WorklogState) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
      {STATE_ORDER.map((s) => {
        const t = time.by_state[s];
        const meta = STATE[s];
        return (
          <div
            key={s}
            onClick={() => onPick(s)}
            title={meta.help}
            style={{
              cursor: 'pointer', padding: 12, borderRadius: 'var(--radius-sm)',
              border: active === s ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border)',
              background: 'var(--color-surface)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <Badge variant={meta.variant}>{meta.label}</Badge>
            </div>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{fmtHours(t.hours)}</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              {/* Non-billable has no value by definition — showing $0 would
                  read as a failure to price it rather than a choice. */}
              {s === 'non_billable' ? `${t.worklog_count} worklog(s)` : `${fmtCurrency(t.value)} · ${t.worklog_count} worklog(s)`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BucketTable({ time, view }: { time: WorkspaceTime; view: Exclude<TimeView, 'worklogs'> }) {
  const rows = view === 'role'
    ? time.by_role.map((r) => ({ key: r.role, label: r.role, ...r }))
    : view === 'person'
      ? time.by_person.map((p) => ({ key: p.jira_user_id ?? p.name ?? '?', label: p.name ?? '(unknown)', ...p }))
      : view === 'deliverable'
        ? time.by_deliverable.map((d) => ({ key: d.jira_issue_id, label: `${d.jira_issue_key ?? d.jira_issue_id} ${d.summary ?? ''}`, ...d }))
        : time.by_week.map((w) => ({ key: w.week_start, label: w.week_start, ...w }));

  if (rows.length === 0) return <EmptyState message="Nothing to show." />;

  return (
    <div style={{ maxHeight: 'calc(100vh - 460px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <Th>{view === 'role' ? 'Role' : view === 'person' ? 'Person' : view === 'deliverable' ? 'Deliverable' : 'Week starting'}</Th>
            <Th right>Logged</Th>
            <Th right>Billed</Th>
            <Th right>Unbilled</Th>
            <Th right>Needs review</Th>
            <Th right>Non-billable</Th>
            <Th right>Value</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Tr key={r.key}>
              <Td bold>{r.label}</Td>
              <Td right muted>{fmtHours(r.hours)}</Td>
              <Td right>{r.billed_hours > 0 ? fmtHours(r.billed_hours) : '—'}</Td>
              <Td right style={{ color: r.unbilled_hours > 0 ? 'var(--color-red-text)' : undefined, fontWeight: r.unbilled_hours > 0 ? 500 : undefined }}>
                {r.unbilled_hours > 0 ? fmtHours(r.unbilled_hours) : '—'}
              </Td>
              <Td right style={{ color: r.needs_review_hours > 0 ? 'var(--color-amber-text)' : undefined }}>
                {r.needs_review_hours > 0 ? fmtHours(r.needs_review_hours) : '—'}
              </Td>
              <Td right muted>{r.non_billable_hours > 0 ? fmtHours(r.non_billable_hours) : '—'}</Td>
              <Td right>{fmtCurrency(r.value)}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function WorklogTable({ rows, filter, onFilter, truncated }: {
  rows: WorkspaceTimeRow[]; filter: WorklogState | 'all';
  onFilter: (s: WorklogState | 'all') => void; truncated: boolean;
}) {
  const filtered = useMemo(
    () => (filter === 'all' ? rows : rows.filter((r) => r.state === filter)),
    [rows, filter],
  );
  // Newest first; the column headers stay static in phase 1 rather than
  // shipping half-wired sort affordances.
  const { sorted } = useTableSort(filtered, {
    date: (r) => r.work_date,
  }, { key: 'date', dir: 'desc' });

  return (
    <>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={() => onFilter('all')}
          style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
            border: filter === 'all' ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
            background: 'var(--color-surface)', color: 'var(--color-text-muted)' }}
        >
          All ({rows.length})
        </button>
        {STATE_ORDER.map((s) => (
          <button
            key={s}
            onClick={() => onFilter(s)}
            title={STATE[s].help}
            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              border: filter === s ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
              background: 'var(--color-surface)', color: 'var(--color-text-muted)' }}
          >
            {STATE[s].label} ({rows.filter((r) => r.state === s).length})
          </button>
        ))}
      </div>

      {truncated && (
        <div style={{ fontSize: 10, color: 'var(--color-amber-text)' }}>
          Showing the most recent 5,000 worklogs. The totals above cover every worklog — export for the full list.
        </div>
      )}

      {sorted.length === 0 ? <EmptyState message="No worklogs in this state." /> : (
        <div style={{ maxHeight: 'calc(100vh - 500px)', overflow: 'auto' }}>
          <Table>
            <thead>
              <tr>
                <Th>Date</Th><Th>Person</Th><Th>Deliverable</Th>
                <Th right>Logged</Th><Th right>Billable</Th><Th right>Value</Th>
                <Th>State</Th><Th>Invoice</Th><Th>Description</Th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <Tr key={r.tempo_worklog_id}>
                  <Td muted style={{ fontSize: 11 }}>{r.work_date}</Td>
                  <Td style={{ fontSize: 11 }}>{r.author_name ?? '—'}</Td>
                  <Td style={{ fontSize: 11 }}>
                    <span style={{ marginRight: 5 }}>{r.deliverable_key ?? '—'}</span>
                    <span style={{ color: 'var(--color-text-muted)' }}>{r.deliverable_summary}</span>
                  </Td>
                  <Td right muted style={{ fontSize: 11 }}>{r.logged_hours.toFixed(2)}</Td>
                  <Td right style={{ fontSize: 11 }}>{r.billable_hours.toFixed(2)}</Td>
                  <Td right style={{ fontSize: 11 }}>{r.value > 0 ? fmtCurrency(r.value) : '—'}</Td>
                  <Td><Badge variant={STATE[r.state].variant}>{STATE[r.state].label}</Badge></Td>
                  <Td muted style={{ fontSize: 11 }}>{r.invoice_number ?? r.chargeover_invoice_id ?? '—'}</Td>
                  <Td muted style={{ fontSize: 11, maxWidth: 320 }}>{r.description || '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
}

/** Worklog-level export — the grain everything else is rolled up from, so a
 *  spreadsheet can reproduce any of the views above. */
function exportTime(engagementKey: string, time: WorkspaceTime): void {
  const rows: (string | number)[][] = [[
    'Worklog ID', 'Date', 'Person', 'Deliverable', 'Deliverable summary',
    'Logged hours', 'Billable hours', 'Non-billable hours', 'Rate', 'Value',
    'State', 'Invoice', 'Description',
  ]];
  for (const r of time.rows) {
    rows.push([
      r.tempo_worklog_id, r.work_date, r.author_name ?? '',
      r.deliverable_key ?? '', r.deliverable_summary ?? '',
      r.logged_hours.toFixed(2), r.billable_hours.toFixed(2), r.non_billable_hours.toFixed(2),
      r.billable_rate != null ? r.billable_rate.toFixed(2) : '',
      r.value.toFixed(2), STATE[r.state].label,
      r.invoice_number ?? r.chargeover_invoice_id ?? '', r.description ?? '',
    ]);
  }
  downloadCsv(`${engagementKey}-time.csv`, rows);
}
